import { useState, useEffect } from "react";
import { ref, onValue, off, update, get } from "firebase/database";
import { database } from "../constants/firebase";
import { useParams } from "react-router";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import { BsPersonCheck, BsPersonX, BsLink45Deg, BsBriefcase } from "react-icons/bs";
import { MdOutlineCheck } from "react-icons/md";

import JobCheckout from "../components/JobCheckout";
import { ROUTES } from "../App";

const JobApplicants = () => {
    const { jobId } = useParams();
    const { currentUser } = useUser();
    const navigate = useMoveTo();
    const [job, setJob] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selecting, setSelecting] = useState(null);

    // Checkout State
    const [checkoutData, setCheckoutData] = useState({
        isOpen: false,
        applicant: null,
        amount: 0
    });

    // Load job details
    useEffect(() => {
        if (!jobId) return;
        get(ref(database, `Jobs/${jobId}`))
            .then((snap) => {
                if (snap.exists()) setJob({ id: jobId, ...snap.val() });
            })
            .catch((e) => console.error("Load job error:", e));
    }, [jobId]);

    // Load applicants in real-time
    useEffect(() => {
        if (!jobId) return;
        const appRef = ref(database, `JobApplications/${jobId}`);
        onValue(
            appRef,
            (snap) => {
                if (snap.exists()) {
                    const data = snap.val();
                    const list = Object.entries(data)
                        .map(([uid, app]) => ({ uid, ...app }))
                        .sort((a, b) => b.appliedAt - a.appliedAt);
                    setApplicants(list);
                } else {
                    setApplicants([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Applicants fetch error:", error);
                setLoading(false);
            }
        );
        return () => off(appRef);
    }, [jobId]);

    const handleSelect = async (applicant) => {
        if (selecting) return;
        if (job?.status === "closed") {
            window.showToast("This job is closed.", "info");
            return;
        }
        if (job?.hiredApplicantId && job.hiredApplicantId !== applicant.uid) {
            window.showToast("A creator has already been hired for this job.", "info");
            return;
        }
        const isAlreadySelected = applicant.status === "selected";
        const newStatus = isAlreadySelected ? "applied" : "selected";
        setSelecting(applicant.uid);
        try {
            await update(ref(database, `JobApplications/${jobId}/${applicant.uid}`), {
                status: newStatus,
            });
            window.showToast(
                isAlreadySelected ? "Selection removed." : "Applicant selected!",
                isAlreadySelected ? "info" : "success"
            );
        } catch (e) {
            console.error("Select applicant error:", e);
            window.showToast("Failed to update. Try again.", "error");
        } finally {
            setSelecting(null);
        }
    };

    const handleHire = async (applicant) => {
        try {
            if (job?.status === "closed") {
                window.showToast("This job is already closed.", "info");
                return;
            }
            if (job?.hiredApplicantId) {
                window.showToast("A creator has already been hired for this job.", "info");
                return;
            }

            // Try to parse the numeric value from the job budget
            let amount = 0;
            if (job?.budget) {
                const matches = job.budget.match(/\d+/g);
                if (matches && matches.length > 0) {
                    amount = parseFloat(matches[0].replace(/,/g, ''));
                }
            }

            // Instead of prompt, we open the modal directly with the parsed amount
            // If the user needs to adjust it, we can allow editing in the checkout modal
            const finalAmount = amount || 0;
            
            // 🔥 Fetch full applicant user data to get payout details
            const userSnap = await get(ref(database, `Users/${applicant.uid}`));
            const fullApplicantData = userSnap.exists() ? { ...applicant, ...userSnap.val() } : applicant;

            setCheckoutData({
                isOpen: true,
                applicant: fullApplicantData,
                amount: finalAmount
            });
        } catch (err) {
            console.error("Handle hire error:", err);
            window.showToast("Failed to prepare checkout.", "error");
        }
    };

    const selectedCount = applicants.filter((a) => a.status === "selected").length;
    const isJobClosed = job?.status === "closed";

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            <MobileHeader
                title={job ? `${job.title} — Applicants` : "Applicants"}
                isBackBtn
            />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto pb-24 md:pb-5 px-4 pt-5 bg-white dark:bg-[#1A1A1A]">
                    {/* Desktop header */}
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <div className="hidden md:block text-center">
                            <h2 className="text-2xl md:text-3xl font-semibold">
                                {job ? job.title : "Applicants"}
                            </h2>
                            {job && (
                                <p className="text-sm text-gray-400 mt-0.5">
                                    {job.role} · {job.location}
                                </p>
                            )}
                        </div>
                        <div className="w-5" />
                    </div>

                    {/* Stats bar */}
                    {!loading && (
                        <div className="flex items-center gap-4 mb-5">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold text-black dark:text-white">
                                    {applicants.length}
                                </span>{" "}
                                applicant{applicants.length !== 1 ? "s" : ""}
                            </span>
                            {selectedCount > 0 && (
                                <span className="text-sm text-[#327691] font-semibold flex items-center gap-1">
                                    <MdOutlineCheck className="w-4 h-4" />
                                    {selectedCount} selected
                                </span>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center pt-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#327691]" />
                        </div>
                    ) : applicants.length === 0 ? (
                        <div className="flex flex-col items-center pt-20 text-center">
                            <BsBriefcase className="w-14 h-14 text-gray-300 dark:text-gray-600 mb-4" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                                No applicants yet
                            </p>
                            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                                Applications will appear here once people apply.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {applicants.map((applicant) => {
                                const isSelected = applicant.status === "selected";
                                const isHired =
                                    applicant.status === "hired" ||
                                    job?.hiredApplicantId === applicant.uid;
                                const isLoading = selecting === applicant.uid;
                                return (
                                    <div
                                        key={applicant.uid}
                                        className={`p-4 rounded-2xl border transition-all ${
                                            isHired
                                                ? "border-green-400/60 bg-green-50 dark:bg-green-900/10"
                                                : isSelected
                                                ? "border-[#327691] bg-[#327691]/5 dark:bg-[#327691]/10"
                                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424]"
                                        }`}
                                    >
                                        {/* Applicant header */}
                                        <div className="flex items-center gap-3 mb-3">
                                            {applicant.applicantProfilePic ? (
                                                <img
                                                    src={applicant.applicantProfilePic}
                                                    alt={applicant.applicantName}
                                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-[#327691]/20 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-[#327691] font-semibold text-sm">
                                                        {applicant.applicantName?.[0]?.toUpperCase() || "?"}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-semibold text-sm truncate">
                                                        {applicant.applicantName}
                                                    </p>
                                                    {isHired ? (
                                                        <span className="flex items-center gap-1 text-[10px] font-bold text-white bg-green-600 px-2 py-0.5 rounded-full">
                                                            <MdOutlineCheck className="w-3 h-3" />
                                                            Hired
                                                        </span>
                                                    ) : isSelected && (
                                                        <span className="flex items-center gap-1 text-[10px] font-bold text-white bg-[#327691] px-2 py-0.5 rounded-full">
                                                            <MdOutlineCheck className="w-3 h-3" />
                                                            Selected
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-400">
                                                    Applied{" "}
                                                    {new Date(applicant.appliedAt).toLocaleDateString(
                                                        "en-US",
                                                        {
                                                            month: "short",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        }
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Cover message */}
                                        {applicant.coverMessage && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3 whitespace-pre-line line-clamp-4">
                                                {applicant.coverMessage}
                                            </p>
                                        )}

                                        {/* Actions row */}
                                        <div className="flex items-center gap-2 mt-2">
                                            {applicant.portfolioLink && (
                                                <a
                                                    href={applicant.portfolioLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 text-xs text-[#327691] font-semibold px-3 py-1.5 rounded-lg border border-[#327691]/40 hover:bg-[#327691]/10 transition-colors"
                                                >
                                                    <BsLink45Deg className="w-4 h-4" />
                                                    Portfolio
                                                </a>
                                            )}
                                            <button
                                                onClick={() => navigate(`${ROUTES.VIEW_PROFILE}/${applicant.uid}`, { state: { from: 'job-applicants', jobId, job } })}
                                                className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                            >
                                                <BsPersonCheck className="w-4 h-4" />
                                                View Profile
                                            </button>
                                            <div className="ml-auto flex items-center gap-2">
                                                {isSelected && !isHired && !isJobClosed && (
                                                    <button
                                                        onClick={() => handleHire(applicant)}
                                                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#327691] text-white hover:bg-[#27607a] transition-all"
                                                    >
                                                        <BsPersonCheck className="w-3.5 h-3.5" />
                                                        Secure & Hire
                                                    </button>
                                                )}
                                                {isHired ? (
                                                    <span className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-green-300 text-green-600 dark:text-green-400">
                                                        Hired
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleSelect(applicant)}
                                                        disabled={isLoading || isJobClosed}
                                                        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all border ${
                                                            isSelected
                                                                ? "border-red-200 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
                                                                : "border-[#327691] text-white bg-[#327691] hover:bg-[#27607a]"
                                                        } disabled:opacity-50`}
                                                    >
                                                        {isLoading ? (
                                                            <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin inline-block" />
                                                        ) : isSelected ? (
                                                            <>
                                                                <BsPersonX className="w-3.5 h-3.5" />
                                                                Deselect
                                                            </>
                                                        ) : (
                                                            <>
                                                                <BsPersonCheck className="w-3.5 h-3.5" />
                                                                Select
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </main>
            </div>

                    {/* Job Checkout Modal */}
            <JobCheckout 
                isOpen={checkoutData.isOpen}
                onClose={() => setCheckoutData({ ...checkoutData, isOpen: false })}
                job={job}
                applicant={checkoutData.applicant}
                basePrice={checkoutData.amount}
            />
        </div>
    );
};

export default JobApplicants;
